import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Typography,
  IconButton,
  Card,
  CardContent,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';

import Navigation from './Navigation';
import RegisterStat from './RegisterStat';

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

/** --------------------------
 *  Desktop row (table)
 *  -------------------------*/
const SortableTableRow = ({ doc, onGoTo, dataDocuments, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: doc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1
  };

  const realIndex = dataDocuments.findIndex(d => d.id === doc.id);

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: '33%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Drag handle */}
          <IconButton
            aria-label="Drag to reorder"
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', touchAction: 'none' }}
          >
            <DragIndicator />
          </IconButton>
          <span>{doc.file.name}</span>
        </Box>
      </TableCell>

      <TableCell sx={{ width: '33%' }}>
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {Object.entries(doc.metadata).map(([key, value]) => (
            <li key={key}>
              <strong>{key}:</strong> {String(value)}
            </li>
          ))}
        </ul>
      </TableCell>

      <TableCell sx={{ width: '33%', textAlign: 'center' }}>
        <Box
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
        >
          <IconButton
            aria-label="Edit document"
            onClick={() => {
              if (realIndex !== -1) onGoTo('annotate', realIndex);
            }}
            size="small"
          >
            <EditOutlined />
          </IconButton>
          <IconButton
            aria-label="Delete document"
            onClick={() => onDelete(doc.id)}
            size="small"
          >
            <DeleteOutline />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/** --------------------------
 *  Mobile item (card)
 *  -------------------------*/
const SortableCardItem = ({ doc, onGoTo, dataDocuments, onDelete, onMoveUp, onMoveDown }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: doc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1
  };

  const realIndex = dataDocuments.findIndex(d => d.id === doc.id);

  return (
    <Card ref={setNodeRef} variant="outlined" sx={{ mb: 1 }} style={style}>
      <CardContent sx={{ py: 1.5 }}>
        {/* Header row: drag handle + filename */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            aria-label="Drag to reorder"
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', touchAction: 'none' }}
          >
            <DragIndicator />
          </IconButton>
          <Typography
            variant="subtitle2"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}
            title={doc.file.name}
          >
            {doc.file.name}
          </Typography>

          {/* Reorder fallback buttons */}
          <IconButton aria-label="Move up" size="small" onClick={() => onMoveUp(doc.id)}>
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton aria-label="Move down" size="small" onClick={() => onMoveDown(doc.id)}>
            <ArrowDownward fontSize="small" />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Metadata */}
        <Box sx={{ fontSize: 14, lineHeight: 1.5 }}>
          {Object.keys(doc.metadata || {}).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No metadata</Typography>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {Object.entries(doc.metadata).map(([key, value]) => (
                <li key={key}><strong>{key}:</strong> {String(value)}</li>
              ))}
            </ul>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <IconButton
            aria-label="Edit document"
            size="small"
            onClick={() => {
              if (realIndex !== -1) onGoTo('annotate', realIndex);
            }}
          >
            <EditOutlined />
          </IconButton>
          <IconButton
            aria-label="Delete document"
            size="small"
            onClick={() => onDelete(doc.id)}
          >
            <DeleteOutline />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const ReviewPage = ({ update, data, onNext, onBack, onGoTo }) => {
  const [docs, setDocs] = useState(data.documents || []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sensors tuned for touch
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 }
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = docs.findIndex(doc => doc.id === active.id);
    const newIndex = docs.findIndex(doc => doc.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newDocs = arrayMove(docs, oldIndex, newIndex);
    setDocs(newDocs);
    update({ documents: newDocs });
  };

  // Non-DnD fallback helpers (used in mobile cards)
  const moveBy = (id, delta) => {
    const index = docs.findIndex(d => d.id === id);
    if (index < 0) return;
    const target = index + delta;
    if (target < 0 || target >= docs.length) return;
    const newDocs = arrayMove(docs, index, target);
    setDocs(newDocs);
    update({ documents: newDocs });
  };
  const handleMoveUp = (id) => moveBy(id, -1);
  const handleMoveDown = (id) => moveBy(id, +1);

  const handleDelete = (id) => {
    const newDocs = docs.filter(d => d.id !== id);
    setDocs(newDocs);
    update({ documents: newDocs });
  };

  if (!docs || docs.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          No documents found. Please add documents before continuing.
        </Typography>
        <Button variant="outlined" onClick={() => onGoTo('start')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <RegisterStat
        endpoint="https://stats.soliman.nl/register"
        site="binder.soliman.nl"
        app="Binder"
        module="Review"
      />
      <Navigation currentKey="review" onGoTo={onGoTo} />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        {/* Desktop: table layout */}
        {!isMobile && (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '33%' }}>
                    File Name (Drag to reorder)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '33%' }}>
                    Metadata
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '33%', textAlign: 'center' }}>
                    <EditOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
                    <DeleteOutline sx={{ verticalAlign: 'middle' }} />
                  </TableCell>
                </TableRow>
              </TableHead>

              <SortableContext
                items={docs.map(doc => doc.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {docs.map((doc) => (
                    <SortableTableRow
                      key={doc.id}
                      doc={doc}
                      dataDocuments={data.documents}
                      onGoTo={onGoTo}
                      onDelete={handleDelete}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </TableContainer>
        )}

        {/* Mobile: card list layout */}
        {isMobile && (
          <Box sx={{ mt: 2 }}>
            <SortableContext
              items={docs.map(doc => doc.id)}
              strategy={verticalListSortingStrategy}
            >
              {docs.map((doc) => (
                <SortableCardItem
                  key={doc.id}
                  doc={doc}
                  dataDocuments={data.documents}
                  onGoTo={onGoTo}
                  onDelete={handleDelete}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                />
              ))}
            </SortableContext>
          </Box>
        )}
      </DndContext>

      <Stack direction="row" justifyContent="space-between" mt={4}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={() => onNext(docs)}>
          Next step
        </Button>
      </Stack>
    </Box>
  );
};

export default ReviewPage;
